import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  Clock3,
  ExternalLink,
  Link2,
  Loader2,
  Plus,
  RefreshCw,
  Save,
  Trash2,
  UserRound,
  Video,
} from "lucide-react";
import {
  appointmentSchedulingService,
  type AppointmentAvailabilityRule,
  type AppointmentCapabilityReadinessStatus,
  type AppointmentCapturePreflight,
  type AppointmentFlow,
  type AppointmentFlowCreate,
  type AppointmentGoogleConnection,
  type AppointmentSeller,
  type AppointmentSellerCalendar,
} from "../../../services/appointments/appointmentSchedulingService";
import {
  chatbotV2Service,
  type CommerceFlowApiRow,
  type WhatsAppTemplate,
} from "../../../services/chatbotV2/chatbotV2Service";

type FlowQuestionDraft = {
  key: string;
  question: string;
  raw: Record<string, unknown>;
};

type AutomationTemplateDraft = {
  enabled: boolean;
  templateName: string;
  language: string;
};

type ReminderAutomationDraft = AutomationTemplateDraft & {
  key: string;
  minutesBefore: number;
};

type FlowAutomationDraft = {
  initialNudge: AutomationTemplateDraft & { delayMinutes: number };
  reminders: ReminderAutomationDraft[];
  noShowRecovery: AutomationTemplateDraft & { delayMinutes: number };
};

type ApprovedTemplateOption = {
  key: string;
  name: string;
  language: string;
  label: string;
};

type FlowDraft = {
  id: number | null;
  name: string;
  publicAliasesText: string;
  status: "draft" | "active" | "paused" | "archived";
  timezone: string;
  bookingMode: "qualify_then_book" | "prebook_then_qualify";
  meetingDurationMinutes: number;
  attendanceGraceMinutes: number;
  minimumNoticeMinutes: number;
  bookingWindowDays: number;
  financialCriterionEnabled: boolean;
  financialCriterion: string;
  awarenessCriterionEnabled: boolean;
  awarenessCriterion: string;
  fitCriterionEnabled: boolean;
  fitCriterion: string;
  qualificationInstructions: string;
  questions: FlowQuestionDraft[];
  rejectionAction: "politely_decline" | "sell_product";
  rejectionFunnelId: string;
  communicablePrice: string;
  communicablePriceCurrency: string;
  hidePriceUntilMeeting: boolean;
  captureNoticeText: string;
  captureConsentMode: "notice" | "explicit_ack";
  artifactRetentionDays: number;
  automations: FlowAutomationDraft;
};

type SellerDraft = {
  displayName: string;
  email: string;
  timezone: string;
  status: "active" | "inactive" | "disconnected";
  meetingDurationMinutes: number;
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  maxDailyMeetings: string;
  roundRobinWeight: number;
};

type AvailabilityDraft = {
  sellerId: string;
  flowId: string;
  dayOfWeek: number;
  startLocalTime: string;
  endLocalTime: string;
};

const DEFAULT_QUESTIONS = [
  "¿Cuál es la situación actual de tu negocio y qué te gustaría cambiar?",
  "¿Qué resultado quieres lograr y en qué plazo?",
  "¿Cuál es el principal obstáculo que hoy te impide conseguirlo?",
  "¿Por qué es importante resolverlo ahora?",
  "Si confirmamos que encaja, ¿qué capacidad tienes para invertir en resolverlo?",
  "Además de ti, ¿quién participa en la decisión?",
];

const DEFAULT_QUALIFICATION_INSTRUCTIONS =
  "Conoce el negocio con una conversación natural, una pregunta por vez y sin hacer sentir juzgada a la persona. Adapta la siguiente pregunta a lo que ya respondió. Antes de confirmar, valida que entiende el propósito de la reunión y consigue un compromiso explícito de asistencia.";

const DEFAULT_CAPTURE_NOTICE =
  "Esta reunión solicita grabación y transcripción automáticas. Google también mostrará sus avisos al entrar. Si no estás de acuerdo, avisa antes de la reunión.";

const newQuestion = (question = "", raw: Record<string, unknown> = {}): FlowQuestionDraft => ({
  key: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  question,
  raw,
});

const newReminder = (
  minutesBefore = 60,
  templateName = "",
  language = "es",
  enabled = false
): ReminderAutomationDraft => ({
  key: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  enabled,
  minutesBefore,
  templateName,
  language,
});

const approvedTemplateKey = (name: string, language: string) =>
  JSON.stringify([name, language]);

const publicAliasesFromText = (value: string) =>
  Array.from(
    new Set(
      value
        // El contrato admite cualquier texto de hasta 80 caracteres. Separar por
        // comas rompería silenciosamente aliases válidos al volver a guardarlos.
        .split(/\r?\n/)
        .map((alias) => alias.trim())
        .filter(Boolean)
    )
  );

const emptyFlowDraft = (): FlowDraft => ({
  id: null,
  name: "Diagnóstico high ticket",
  publicAliasesText: "",
  status: "draft",
  timezone: "America/Bogota",
  bookingMode: "qualify_then_book",
  meetingDurationMinutes: 30,
  attendanceGraceMinutes: 15,
  minimumNoticeMinutes: 60,
  bookingWindowDays: 30,
  financialCriterionEnabled: true,
  financialCriterion: "",
  awarenessCriterionEnabled: true,
  awarenessCriterion: "",
  fitCriterionEnabled: true,
  fitCriterion: "",
  qualificationInstructions: DEFAULT_QUALIFICATION_INSTRUCTIONS,
  questions: DEFAULT_QUESTIONS.map((question) => newQuestion(question)),
  rejectionAction: "politely_decline",
  rejectionFunnelId: "",
  communicablePrice: "",
  communicablePriceCurrency: "COP",
  hidePriceUntilMeeting: false,
  captureNoticeText: DEFAULT_CAPTURE_NOTICE,
  captureConsentMode: "explicit_ack",
  artifactRetentionDays: 365,
  automations: {
    initialNudge: {
      enabled: false,
      delayMinutes: 3,
      templateName: "",
      language: "es",
    },
    reminders: [],
    noShowRecovery: {
      enabled: false,
      delayMinutes: 15,
      templateName: "",
      language: "es",
    },
  },
});

const sellerToDraft = (seller: AppointmentSeller): SellerDraft => ({
  displayName: seller.display_name,
  email: seller.email,
  timezone: seller.timezone,
  status:
    seller.status === "inactive" || seller.status === "disconnected"
      ? seller.status
      : "active",
  meetingDurationMinutes: seller.meeting_duration_minutes,
  bufferBeforeMinutes: seller.buffer_before_minutes,
  bufferAfterMinutes: seller.buffer_after_minutes,
  maxDailyMeetings: seller.max_daily_meetings == null ? "" : String(seller.max_daily_meetings),
  roundRobinWeight: seller.round_robin_weight,
});

const flowToDraft = (flow: AppointmentFlow): FlowDraft => ({
  id: flow.id,
  name: flow.name,
  publicAliasesText: (flow.public_aliases || []).join("\n"),
  status:
    flow.status === "active" || flow.status === "paused" || flow.status === "archived"
      ? flow.status
      : "draft",
  timezone: flow.timezone,
  bookingMode: flow.booking_mode === "prebook_then_qualify" ? "prebook_then_qualify" : "qualify_then_book",
  meetingDurationMinutes: flow.meeting_duration_minutes,
  attendanceGraceMinutes: flow.attendance_grace_minutes,
  minimumNoticeMinutes: flow.minimum_notice_minutes,
  bookingWindowDays: flow.booking_window_days,
  financialCriterionEnabled: flow.financial_criterion_enabled,
  financialCriterion: flow.financial_criterion || "",
  awarenessCriterionEnabled: flow.awareness_criterion_enabled,
  awarenessCriterion: flow.awareness_criterion || "",
  fitCriterionEnabled: flow.fit_criterion_enabled,
  fitCriterion: flow.fit_criterion || "",
  qualificationInstructions: flow.qualification_instructions || "",
  questions: (flow.qualification_questions_json || []).map((value) => {
    if (typeof value === "string") return newQuestion(value);
    const raw = value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
    return newQuestion(String(raw.question || raw.text || ""), raw);
  }),
  rejectionAction: flow.rejection_action === "sell_product" ? "sell_product" : "politely_decline",
  rejectionFunnelId: flow.rejection_funnel_id || "",
  communicablePrice: flow.communicable_price == null ? "" : String(flow.communicable_price),
  communicablePriceCurrency: flow.communicable_price_currency || "COP",
  hidePriceUntilMeeting: flow.hide_price_until_meeting,
  captureNoticeText: flow.capture_notice_text || "",
  captureConsentMode: flow.capture_consent_mode === "notice" ? "notice" : "explicit_ack",
  artifactRetentionDays: flow.artifact_retention_days,
  automations: {
    initialNudge: {
      enabled: flow.automations?.initial_nudge?.enabled === true,
      delayMinutes: flow.automations?.initial_nudge?.delay_minutes ?? 3,
      templateName: flow.automations?.initial_nudge?.template_name || "",
      language: flow.automations?.initial_nudge?.language || "es",
    },
    reminders: (flow.automations?.reminders || []).map((reminder) =>
      newReminder(
        reminder.minutes_before,
        reminder.template_name,
        reminder.language || "es",
        reminder.enabled === true
      )
    ),
    noShowRecovery: {
      enabled: flow.automations?.no_show_recovery?.enabled === true,
      delayMinutes: flow.automations?.no_show_recovery?.delay_minutes ?? 15,
      templateName: flow.automations?.no_show_recovery?.template_name || "",
      language: flow.automations?.no_show_recovery?.language || "es",
    },
  },
});

type ApiErrorShape = {
  response?: {
    status?: number;
    data?: {
      code?: unknown;
      detail?: unknown;
      message?: unknown;
    };
  };
  message?: string;
};

const apiErrorCode = (error: unknown) => {
  const payload = error as ApiErrorShape;
  const detail = payload.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && "code" in detail) {
    const code = (detail as { code?: unknown }).code;
    if (typeof code === "string") return code;
  }
  return typeof payload.response?.data?.code === "string" ? payload.response.data.code : "";
};

const apiErrorStatus = (error: unknown) => {
  const status = (error as ApiErrorShape).response?.status;
  return typeof status === "number" ? status : null;
};

const apiErrorMessage = (error: unknown) => {
  const payload = error as ApiErrorShape;
  const code = apiErrorCode(error);
  const known: Record<string, string> = {
    appointments_not_found: "Agendamientos todavía no está habilitado para esta cuenta.",
    appointment_configuration_not_found: "La configuración cambió o ya no existe. Recarga e intenta de nuevo.",
    invalid_appointment_configuration: "Revisa los datos: hay una configuración que no es válida.",
    appointment_configuration_conflict: "Ese registro entra en conflicto con una configuración existente.",
    google_oauth_connection_not_found: "La conexión de Google ya no existe.",
    google_seller_oauth_not_configured: "La conexión de Google para vendedores aún no está configurada en este entorno.",
    google_workspace_admin_oauth_not_configured: "La conexión de Google Workspace para auditoría aún no está configurada en este entorno.",
    google_seller_redirect_uri_invalid: "La dirección de retorno de Google para vendedores no coincide con la configuración del entorno.",
    google_workspace_admin_redirect_uri_invalid: "La dirección de retorno de Google Workspace para auditoría no coincide con la configuración del entorno.",
    google_oauth_completion_url_not_configured: "La dirección de regreso a Fluxi aún no está configurada en este entorno.",
    google_oauth_completion_url_invalid: "La dirección de regreso a Fluxi no es válida en este entorno.",
    capture_preflight_reauthorization_required: "Reconecta Google para autorizar la creación del Meet privado de prueba.",
    google_capture_preflight_reauthorization_required: "Reconecta Google para autorizar la creación del Meet privado de prueba.",
    capture_preflight_connection_not_ready: "Completa primero la conexión de Calendar y los permisos de Meet.",
    capture_preflight_already_running: "Ya hay una prueba de captura en curso para este vendedor.",
  };
  const responseMessage = payload.response?.data?.message;
  if (code && !known[code]) {
    console.error("Appointment scheduling request failed", {
      code,
      status: payload.response?.status,
    });
  }
  return known[code]
    || (code ? "No fue posible completar la operación. Revisa la configuración e intenta de nuevo." : "")
    || (typeof responseMessage === "string" ? responseMessage : "")
    || payload.message
    || "No fue posible completar la operación.";
};

const capturePreflightErrorMessage = (error: unknown) => {
  const status = apiErrorStatus(error);
  if (status === 404) {
    return "La verificación privada de Meet todavía no está disponible en este entorno. Calendar puede seguir conectado, pero este vendedor no recibirá citas hasta que grabación y transcripción queden verificadas.";
  }
  if (status === 409) {
    return apiErrorCode(error)
      ? apiErrorMessage(error)
      : "Google necesita reconectarse o completar su preparación antes de iniciar la prueba.";
  }
  return apiErrorMessage(error);
};

const persistedCapturePreflightErrorMessage = (code: string) => {
  const known: Record<string, string> = {
    google_capture_scopes_missing: "La conexión no tiene todos los permisos de captura. Reconecta Google y vuelve a iniciar la prueba.",
    capture_failed: "La prueba no pudo comprobar grabación y transcripción. Revisa la licencia y las políticas de Google antes de reintentar.",
  };
  if (!known[code]) {
    console.error("Appointment capture preflight failed", { code });
  }
  return known[code]
    || "Google no pudo completar la prueba de captura. Revisa permisos, licencia y políticas antes de reintentar.";
};

const Toggle = ({
  checked,
  onChange,
  label,
  disabled = false,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition ${
      checked ? "bg-gray-950" : "bg-gray-300"
    } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
  >
    <span
      className={`size-5 rounded-full bg-white shadow-sm transition-transform ${
        checked ? "translate-x-[22px]" : "translate-x-0.5"
      }`}
    />
  </button>
);

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <span className="mb-1.5 block text-xs font-semibold text-gray-600">{children}</span>
);

const textFieldClass =
  "h-10 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-950 outline-none transition placeholder:text-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-gray-100";
const textAreaClass =
  "min-h-24 w-full resize-y rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm leading-6 text-gray-950 outline-none transition placeholder:text-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-gray-100";

const ApprovedTemplateSelect = ({
  name,
  language,
  options,
  unavailable,
  onChange,
}: {
  name: string;
  language: string;
  options: ApprovedTemplateOption[];
  unavailable: boolean;
  onChange: (template: ApprovedTemplateOption) => void;
}) => (
  <div>
    <select
      className={textFieldClass}
      value={name ? approvedTemplateKey(name, language) : ""}
      disabled={unavailable || options.length === 0}
      onChange={(event) => {
        const selected = options.find((option) => option.key === event.target.value);
        if (selected) onChange(selected);
      }}
    >
      <option value="">
        {unavailable
          ? "Plantillas no disponibles"
          : options.length
            ? "Seleccionar plantilla aprobada"
            : "No hay plantillas aprobadas"}
      </option>
      {options.map((option) => (
        <option key={option.key} value={option.key}>{option.label}</option>
      ))}
    </select>
    {name ? <span className="mt-1 block text-[11px] text-gray-500">Idioma configurado: {language}</span> : null}
  </div>
);

const Section = ({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) => (
  <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
    <div className="border-b border-gray-100 pb-4">
      <h4 className="text-sm font-semibold text-gray-950">{title}</h4>
      <p className="mt-1 text-sm leading-6 text-gray-500">{description}</p>
    </div>
    <div className="pt-5">{children}</div>
  </section>
);

const SaveButton = ({ loading, label = "Guardar" }: { loading: boolean; label?: string }) => (
  <button
    type="submit"
    disabled={loading}
    className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-gray-950 px-5 text-sm font-semibold text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
  >
    {loading ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
    {label}
  </button>
);

const readinessCopy = (connection: AppointmentGoogleConnection) => {
  if (connection.status !== "connected") return { label: "Requiere reconexión", tone: "text-amber-700 bg-amber-50" };
  if (connection.role === "workspace_admin") {
    if (connection.verified_attendance_ready) {
      return { label: "Auditoría de asistencia lista", tone: "text-emerald-700 bg-emerald-50" };
    }
    if (connection.admin_reports_scope_ready) {
      return { label: "Permiso de auditoría listo", tone: "text-blue-700 bg-blue-50" };
    }
    return { label: "Auditoría por comprobar", tone: "text-amber-700 bg-amber-50" };
  }
  if (connection.capture_routable === true) return { label: "Captura verificada", tone: "text-emerald-700 bg-emerald-50" };
  if (connection.capture_ready) return { label: "Evidencia anterior · renovar prueba", tone: "text-amber-700 bg-amber-50" };
  if (connection.scheduling_ready) return { label: "Calendar listo · captura por verificar", tone: "text-blue-700 bg-blue-50" };
  return { label: "Pendiente de comprobar", tone: "text-amber-700 bg-amber-50" };
};

const ACTIVE_CAPTURE_PREFLIGHT_STATUSES = new Set([
  "queued",
  "pending_create",
  "awaiting_seller",
  "in_meeting",
  "observing",
  "processing_artifacts",
]);

const isActiveCapturePreflight = (status?: string | null) =>
  Boolean(status && ACTIVE_CAPTURE_PREFLIGHT_STATUSES.has(status.toLowerCase()));

const capturePreflightPresentation = (
  status?: string | null,
  captureRoutable = false
) => {
  if (captureRoutable) {
    return { label: "Verificación vigente", tone: "border-emerald-200 bg-emerald-50 text-emerald-700" };
  }
  switch (status?.toLowerCase()) {
    case "queued":
    case "pending_create":
      return { label: "Preparando Meet", tone: "border-blue-200 bg-blue-50 text-blue-700" };
    case "awaiting_seller":
      return { label: "Esperando al vendedor", tone: "border-blue-200 bg-blue-50 text-blue-700" };
    case "in_meeting":
    case "observing":
      return { label: "Prueba en curso", tone: "border-blue-200 bg-blue-50 text-blue-700" };
    case "processing_artifacts":
      return { label: "Google procesa los artefactos", tone: "border-blue-200 bg-blue-50 text-blue-700" };
    case "verified":
      return { label: "Evidencia no vigente · renovar", tone: "border-amber-200 bg-amber-50 text-amber-700" };
    case "failed":
      return { label: "La prueba falló", tone: "border-red-200 bg-red-50 text-red-700" };
    case "expired":
      return { label: "La prueba venció", tone: "border-amber-200 bg-amber-50 text-amber-700" };
    default:
      return { label: "Pendiente de verificar", tone: "border-amber-200 bg-amber-50 text-amber-700" };
  }
};

const capabilityPresentation = (status?: AppointmentCapabilityReadinessStatus | string | null) => {
  switch (status) {
    case "verified":
      return { label: "Verificada", tone: "border-emerald-200 bg-emerald-50 text-emerald-700" };
    case "checking":
      return { label: "Comprobando", tone: "border-blue-200 bg-blue-50 text-blue-700" };
    case "unavailable":
      return { label: "No disponible", tone: "border-gray-200 bg-gray-100 text-gray-700" };
    case "error":
      return { label: "Error al comprobar", tone: "border-red-200 bg-red-50 text-red-700" };
    default:
      return { label: "No verificada", tone: "border-amber-200 bg-amber-50 text-amber-700" };
  }
};

const CapabilityState = ({
  label,
  status,
}: {
  label: string;
  status?: AppointmentCapabilityReadinessStatus | string | null;
}) => {
  const presentation = capabilityPresentation(status);
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-100 bg-white px-3 py-2">
      <span className="text-xs font-medium text-gray-700">{label}</span>
      <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${presentation.tone}`}>
        {presentation.label}
      </span>
    </div>
  );
};

const CapturePreflightPanel = ({
  connection,
  preflight,
  error,
  busy,
  checkingReadiness,
  onStart,
}: {
  connection: AppointmentGoogleConnection;
  preflight?: AppointmentCapturePreflight | null;
  error?: string;
  busy: boolean;
  checkingReadiness: boolean;
  onStart: () => void;
}) => {
  const active = isActiveCapturePreflight(preflight?.status);
  const captureRoutable = connection.capture_routable === true;
  const presentation = capturePreflightPresentation(preflight?.status, captureRoutable);
  const canStart = connection.status === "connected"
    && connection.scheduling_ready
    && connection.meet_space_create_scope_granted === true;
  const normalizedStatus = preflight?.status.toLowerCase();
  const terminalAttempt = normalizedStatus === "failed" || normalizedStatus === "expired";
  const automaticRetryUnavailable = terminalAttempt && preflight?.retryable === false;
  const needsRenewal = normalizedStatus === "verified" && !captureRoutable;
  const showJoinUrl = Boolean(preflight?.join_url && active);
  let actionLabel = "Verificar grabación y transcripción";
  if (captureRoutable) actionLabel = "Verificación vigente";
  else if (checkingReadiness) actionLabel = "Comprobando elegibilidad";
  else if (busy) actionLabel = needsRenewal ? "Iniciando renovación" : "Iniciando verificación";
  else if (active) actionLabel = "Verificación en curso";
  else if (needsRenewal) actionLabel = "Renovar verificación";
  else if (terminalAttempt) actionLabel = "Reintentar verificación";

  return (
    <div className="mt-3 rounded-xl border border-gray-200 bg-white p-4 lg:col-span-2">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-3xl">
          <p className="text-sm font-semibold text-gray-950">Prueba privada de grabación y transcripción</p>
          <p className="mt-1 text-xs leading-5 text-gray-500">
            Fluxi crea un Meet temporal desde esta cuenta. El vendedor entra y finaliza una llamada corta; después Google procesa los artefactos y Fluxi comprueba evidencia real antes de habilitarlo para recibir citas.
          </p>
        </div>
        <span className={`shrink-0 rounded-full border px-3 py-1 text-xs font-semibold ${presentation.tone}`}>
          {presentation.label}
        </span>
      </div>

      {!captureRoutable && error ? (
        <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800">
          {error}
        </p>
      ) : null}
      {!captureRoutable && preflight?.error ? (
        <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs leading-5 text-red-700">
          {persistedCapturePreflightErrorMessage(preflight.error)}
        </p>
      ) : null}
      {!captureRoutable && automaticRetryUnavailable ? (
        <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800">
          Fluxi no repetirá automáticamente este intento. Después de corregir la licencia o las políticas de Google —y reconectar la cuenta si hace falta— puedes iniciar una nueva verificación manual.
        </p>
      ) : null}

      {!canStart && !captureRoutable ? (
        <p className="mt-3 rounded-lg bg-gray-50 px-3 py-2 text-xs leading-5 text-gray-600">
          {connection.meet_space_create_scope_granted !== true
            ? "Autoriza captura para que Fluxi pueda crear el Meet privado exacto de la prueba. Una conexión antigua de Calendar no concede este permiso automáticamente."
            : "Comprueba primero que Calendar esté operativo para esta cuenta."}
        </p>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={busy || active || captureRoutable || !canStart}
          onClick={onStart}
          className="inline-flex h-9 items-center gap-2 rounded-full bg-gray-950 px-4 text-sm font-semibold text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {busy || active ? <Loader2 className="size-4 animate-spin" /> : <Video className="size-4" />}
          {actionLabel}
        </button>
        {showJoinUrl && preflight?.join_url ? (
          <a
            href={preflight.join_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-9 items-center gap-2 rounded-full border border-gray-200 px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            <ExternalLink className="size-4" /> Abrir el Meet de prueba
          </a>
        ) : null}
      </div>

      {showJoinUrl ? (
        <ol className="mt-3 list-decimal space-y-1 pl-5 text-xs leading-5 text-gray-600">
          <li>Abre únicamente el enlace entregado por Fluxi con la misma cuenta Google del vendedor.</li>
          <li>Acepta los avisos nativos de Google, habla brevemente y permanece unos minutos en la reunión.</li>
          <li>Finaliza el Meet. Fluxi seguirá comprobando hasta que Google entregue grabación y transcripción.</li>
        </ol>
      ) : active ? (
        <p className="mt-3 text-xs leading-5 text-gray-500">
          La preparación continúa automáticamente. El botón para entrar aparecerá solo cuando la API entregue el enlace exacto; Fluxi no construye ni adivina URLs de Meet.
        </p>
      ) : null}

      {active && preflight?.join_deadline_at ? (
        <p className="mt-2 text-[11px] leading-5 text-gray-500">
          Entra antes de {formatDateTime(preflight.join_deadline_at)}.
        </p>
      ) : null}
      {preflight?.artifact_deadline_at && normalizedStatus === "processing_artifacts" ? (
        <p className="mt-1 text-[11px] leading-5 text-gray-500">
          Google puede procesar los artefactos hasta {formatDateTime(preflight.artifact_deadline_at)}.
        </p>
      ) : null}

      <p className="mt-3 border-t border-gray-100 pt-3 text-[11px] leading-5 text-gray-500">
        La prueba genera contenido real: Google puede guardar la grabación y la transcripción en el Drive del organizador según su licencia y políticas. En esta V1 Fluxi verifica esos artefactos mediante Google Meet; no solicita acceso general para enumerar el Drive. Las notas inteligentes son opcionales y no bloquean la verificación.
      </p>
    </div>
  );
};

const formatDateTime = (value: string) =>
  new Intl.DateTimeFormat("es-CO", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));

const dayNames = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

export const AppointmentSchedulingSettings = () => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [flows, setFlows] = useState<AppointmentFlow[]>([]);
  const [flowDraft, setFlowDraft] = useState<FlowDraft>(emptyFlowDraft);
  const [flowSaving, setFlowSaving] = useState(false);
  const [sellers, setSellers] = useState<AppointmentSeller[]>([]);
  const [sellerDrafts, setSellerDrafts] = useState<Record<number, SellerDraft>>({});
  const [sellerSavingId, setSellerSavingId] = useState<number | "new" | null>(null);
  const [newSeller, setNewSeller] = useState<SellerDraft>({
    displayName: "",
    email: "",
    timezone: "America/Bogota",
    status: "active",
    meetingDurationMinutes: 30,
    bufferBeforeMinutes: 0,
    bufferAfterMinutes: 0,
    maxDailyMeetings: "",
    roundRobinWeight: 1,
  });
  const [connections, setConnections] = useState<AppointmentGoogleConnection[]>([]);
  const [calendarsBySeller, setCalendarsBySeller] = useState<Record<number, AppointmentSellerCalendar[]>>({});
  const [googleAction, setGoogleAction] = useState<string | null>(null);
  const [googleLoadError, setGoogleLoadError] = useState<string | null>(null);
  const [capturePreflights, setCapturePreflights] = useState<Record<number, AppointmentCapturePreflight | null>>({});
  const [capturePreflightErrors, setCapturePreflightErrors] = useState<Record<number, string>>({});
  const [captureReadinessLoading, setCaptureReadinessLoading] = useState<Record<number, boolean>>({});
  const [captureRefreshVersion, setCaptureRefreshVersion] = useState(0);
  const captureLoadedConnectionsRef = useRef<Set<number>>(new Set());
  const captureReadinessSyncedRef = useRef<Set<number>>(new Set());
  const [workspaceAdminEmail, setWorkspaceAdminEmail] = useState("");
  const [availabilityRules, setAvailabilityRules] = useState<AppointmentAvailabilityRule[]>([]);
  const [availabilityDraft, setAvailabilityDraft] = useState<AvailabilityDraft>({
    sellerId: "",
    flowId: "",
    dayOfWeek: 0,
    startLocalTime: "09:00",
    endLocalTime: "17:00",
  });
  const [availabilitySaving, setAvailabilitySaving] = useState(false);
  const [commerceFlows, setCommerceFlows] = useState<CommerceFlowApiRow[]>([]);
  const [commerceFlowsUnavailable, setCommerceFlowsUnavailable] = useState(false);
  const [approvedTemplates, setApprovedTemplates] = useState<WhatsAppTemplate[]>([]);
  const [approvedTemplatesUnavailable, setApprovedTemplatesUnavailable] = useState(false);

  const hydrateSellers = useCallback((rows: AppointmentSeller[]) => {
    setSellers(rows);
    setSellerDrafts(Object.fromEntries(rows.map((seller) => [seller.id, sellerToDraft(seller)])));
    setAvailabilityDraft((current) => ({
      ...current,
      sellerId: current.sellerId || (rows[0] ? String(rows[0].id) : ""),
    }));
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [[loadedFlows, loadedSellers, loadedRules], googleResult] = await Promise.all([
        Promise.all([
          appointmentSchedulingService.listFlows(),
          appointmentSchedulingService.listSellers(),
          appointmentSchedulingService.listAvailabilityRules(),
        ]),
        appointmentSchedulingService.listGoogleConnections()
          .then((rows) => ({ rows, error: null as string | null }))
          .catch((error) => ({ rows: [] as AppointmentGoogleConnection[], error: apiErrorMessage(error) })),
      ]);
      setFlows(loadedFlows);
      setFlowDraft((current) => {
        const currentFlow = current.id ? loadedFlows.find((flow) => flow.id === current.id) : null;
        return currentFlow ? flowToDraft(currentFlow) : loadedFlows[0] ? flowToDraft(loadedFlows[0]) : emptyFlowDraft();
      });
      hydrateSellers(loadedSellers);
      captureLoadedConnectionsRef.current.clear();
      captureReadinessSyncedRef.current.clear();
      setCaptureRefreshVersion((current) => current + 1);
      setConnections(googleResult.rows);
      setGoogleLoadError(googleResult.error);
      setAvailabilityRules(loadedRules);
      const calendarEntries = await Promise.all(
        loadedSellers.map(async (seller) => [
          seller.id,
          await appointmentSchedulingService.listSellerCalendars(seller.id).catch(() => []),
        ] as const)
      );
      setCalendarsBySeller(Object.fromEntries(calendarEntries));
      const [commerceResult, templateResult] = await Promise.all([
        chatbotV2Service.listCommerceFlows("order_capture")
          .then((rows) => ({ rows, unavailable: false }))
          .catch(() => ({ rows: [] as CommerceFlowApiRow[], unavailable: true })),
        chatbotV2Service.listTemplates("APPROVED")
          .then((rows) => ({
            rows: rows.filter((row) => String(row.status).toUpperCase() === "APPROVED"),
            unavailable: false,
          }))
          .catch(() => ({ rows: [] as WhatsAppTemplate[], unavailable: true })),
      ]);
      setCommerceFlows(commerceResult.rows.filter((row) => row.enabled !== false));
      setCommerceFlowsUnavailable(commerceResult.unavailable);
      setApprovedTemplates(templateResult.rows);
      setApprovedTemplatesUnavailable(templateResult.unavailable);
    } catch (error) {
      setLoadError(apiErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [hydrateSellers]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const result = params.get("google_oauth");
    if (!result) return;
    params.delete("google_oauth");
    params.delete("role");
    const nextSearch = params.toString();
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}${window.location.hash}`
    );
    if (result === "connected") toast.success("Google quedó conectado. Comprueba la conexión para activarla.");
    if (result === "denied") toast.error("Google no autorizó la conexión.");
    if (result === "failed") toast.error("No fue posible completar la conexión con Google.");
  }, []);

  const syncCaptureConnectionReadiness = useCallback(async (connectionId: number) => {
    if (captureReadinessSyncedRef.current.has(connectionId)) return;
    captureReadinessSyncedRef.current.add(connectionId);
    setCaptureReadinessLoading((current) => ({ ...current, [connectionId]: true }));
    try {
      const updated = await appointmentSchedulingService.getGoogleConnectionReadiness(connectionId);
      setConnections((current) => current.map((item) => item.id === updated.id ? updated : item));
      setCapturePreflightErrors((current) => {
        const next = { ...current };
        if (updated.capture_routable === true) {
          delete next[connectionId];
        } else {
          next[connectionId] = "La evidencia histórica ya no habilita al vendedor para reparto. Inicia una nueva verificación manual para renovarla.";
        }
        return next;
      });
    } catch (error) {
      captureReadinessSyncedRef.current.delete(connectionId);
      setCapturePreflightErrors((current) => ({
        ...current,
        [connectionId]: `La prueba terminó, pero no fue posible actualizar la elegibilidad del vendedor: ${apiErrorMessage(error)}`,
      }));
    } finally {
      setCaptureReadinessLoading((current) => {
        if (!current[connectionId]) return current;
        const next = { ...current };
        delete next[connectionId];
        return next;
      });
    }
  }, []);

  const fetchCapturePreflight = useCallback(async (connectionId: number) => {
    try {
      const preflight = await appointmentSchedulingService.getGoogleCapturePreflight(connectionId);
      setCapturePreflights((current) => ({ ...current, [connectionId]: preflight }));
      setCapturePreflightErrors((current) => {
        if (!current[connectionId]) return current;
        const next = { ...current };
        delete next[connectionId];
        return next;
      });
      if (preflight?.status === "verified") void syncCaptureConnectionReadiness(connectionId);
      return preflight;
    } catch (error) {
      setCapturePreflightErrors((current) => ({
        ...current,
        [connectionId]: capturePreflightErrorMessage(error),
      }));
      return null;
    }
  }, [syncCaptureConnectionReadiness]);

  const sellerConnectionKey = useMemo(
    () => connections
      .filter((connection) => connection.role === "seller" && connection.status !== "revoked")
      .map((connection) => connection.id)
      .sort((left, right) => left - right)
      .join(","),
    [connections]
  );

  useEffect(() => {
    if (!sellerConnectionKey) return;
    sellerConnectionKey.split(",").forEach((value) => {
      const connectionId = Number(value);
      if (!Number.isFinite(connectionId) || captureLoadedConnectionsRef.current.has(connectionId)) return;
      captureLoadedConnectionsRef.current.add(connectionId);
      void fetchCapturePreflight(connectionId);
    });
  }, [captureRefreshVersion, fetchCapturePreflight, sellerConnectionKey]);

  const activeCaptureConnectionKey = useMemo(
    () => Object.entries(capturePreflights)
      .filter(([, preflight]) => isActiveCapturePreflight(preflight?.status))
      .map(([connectionId]) => Number(connectionId))
      .sort((left, right) => left - right)
      .join(","),
    [capturePreflights]
  );

  useEffect(() => {
    if (!activeCaptureConnectionKey) return;
    const connectionIds = activeCaptureConnectionKey
      .split(",")
      .map(Number)
      .filter(Number.isFinite);
    const intervalId = window.setInterval(() => {
      connectionIds.forEach((connectionId) => void fetchCapturePreflight(connectionId));
    }, 10_000);
    return () => window.clearInterval(intervalId);
  }, [activeCaptureConnectionKey, fetchCapturePreflight]);

  const activeOrderCaptureOptions = useMemo(
    () => commerceFlows.map((flow) => ({
      value: String(flow.flow_key || flow.id),
      label: String(flow.name || flow.flow_key || flow.id),
    })),
    [commerceFlows]
  );

  const approvedTemplateOptions = useMemo<ApprovedTemplateOption[]>(() => {
    const unique = new Map<string, ApprovedTemplateOption>();
    approvedTemplates.forEach((template) => {
      const name = template.name.trim();
      const language = template.language.trim();
      if (!name || !language) return;
      const key = approvedTemplateKey(name, language);
      if (!unique.has(key)) {
        unique.set(key, {
          key,
          name,
          language,
          label: `${name} · ${language}`,
        });
      }
    });
    return Array.from(unique.values()).sort((left, right) =>
      left.label.localeCompare(right.label, "es")
    );
  }, [approvedTemplates]);

  const approvedTemplateKeys = useMemo(
    () => new Set(approvedTemplateOptions.map((template) => template.key)),
    [approvedTemplateOptions]
  );

  const workspaceAdminConnection = connections.find(
    (connection) => connection.role === "workspace_admin" && connection.status !== "revoked"
  );
  const workspaceAdminReadiness = workspaceAdminConnection
    ? readinessCopy(workspaceAdminConnection)
    : null;
  const readySellerCount = sellers.filter((seller) => {
    const connection = connections.find(
      (item) => item.role === "seller" && item.seller_id === seller.id && item.status === "connected"
    );
    const sellerCalendars = calendarsBySeller[seller.id] || [];
    const writeCalendar = sellerCalendars.some(
      (calendar) => calendar.status === "active" && calendar.is_write_calendar
    );
    const blockingCalendar = sellerCalendars.some(
      (calendar) => calendar.status === "active" && calendar.blocks_availability
    );
    return seller.status === "active"
      && Boolean(connection?.scheduling_ready)
      && connection?.capture_routable === true
      && writeCalendar
      && blockingCalendar;
  }).length;

  const commitFlowDraft = (patch: Partial<FlowDraft>) => {
    setFlowDraft((current) => ({ ...current, ...patch }));
  };

  const commitFlowAutomations = (patch: Partial<FlowAutomationDraft>) => {
    setFlowDraft((current) => ({
      ...current,
      automations: { ...current.automations, ...patch },
    }));
  };

  const updateFlowReminder = (
    key: string,
    patch: Partial<ReminderAutomationDraft>
  ) => {
    setFlowDraft((current) => ({
      ...current,
      automations: {
        ...current.automations,
        reminders: current.automations.reminders.map((reminder) =>
          reminder.key === key ? { ...reminder, ...patch } : reminder
        ),
      },
    }));
  };

  const flowPayload = (): AppointmentFlowCreate => {
    const price = flowDraft.communicablePrice.trim();
    const publicAliases = publicAliasesFromText(flowDraft.publicAliasesText);
    return {
      name: flowDraft.name.trim(),
      public_aliases: publicAliases,
      timezone: flowDraft.timezone.trim(),
      status: flowDraft.status,
      booking_mode: flowDraft.bookingMode,
      meeting_duration_minutes: flowDraft.meetingDurationMinutes,
      attendance_grace_minutes: flowDraft.attendanceGraceMinutes,
      minimum_notice_minutes: flowDraft.minimumNoticeMinutes,
      booking_window_days: flowDraft.bookingWindowDays,
      financial_criterion_enabled: flowDraft.financialCriterionEnabled,
      financial_criterion: flowDraft.financialCriterion.trim() || null,
      awareness_criterion_enabled: flowDraft.awarenessCriterionEnabled,
      awareness_criterion: flowDraft.awarenessCriterion.trim() || null,
      fit_criterion_enabled: flowDraft.fitCriterionEnabled,
      fit_criterion: flowDraft.fitCriterion.trim() || null,
      qualification_instructions: flowDraft.qualificationInstructions.trim() || null,
      qualification_questions_json: flowDraft.questions
        .map((question) => ({ ...question.raw, question: question.question.trim() }))
        .filter((question) => question.question),
      rejection_action: flowDraft.rejectionAction,
      rejection_funnel_id:
        flowDraft.rejectionAction === "sell_product" ? flowDraft.rejectionFunnelId || null : null,
      communicable_price: price ? Number(price) : null,
      communicable_price_currency: price ? flowDraft.communicablePriceCurrency.trim().toUpperCase() : null,
      hide_price_until_meeting: flowDraft.hidePriceUntilMeeting,
      price_disclosure_policy: flowDraft.hidePriceUntilMeeting ? "hide_until_meeting" : "show_when_asked",
      capture_notice_text: flowDraft.captureNoticeText.trim() || null,
      capture_consent_mode: flowDraft.captureConsentMode,
      artifact_retention_days: flowDraft.artifactRetentionDays,
      automations: {
        initial_nudge: {
          enabled: flowDraft.automations.initialNudge.enabled,
          delay_minutes: flowDraft.automations.initialNudge.delayMinutes,
          template_name: flowDraft.automations.initialNudge.templateName || null,
          language: flowDraft.automations.initialNudge.language || "es",
        },
        reminders: flowDraft.automations.reminders
          .filter((reminder) => reminder.enabled || reminder.templateName)
          .map((reminder) => ({
            enabled: reminder.enabled,
            minutes_before: reminder.minutesBefore,
            template_name: reminder.templateName,
            language: reminder.language || "es",
          })),
        no_show_recovery: {
          enabled: flowDraft.automations.noShowRecovery.enabled,
          delay_minutes: flowDraft.automations.noShowRecovery.delayMinutes,
          template_name: flowDraft.automations.noShowRecovery.templateName || null,
          language: flowDraft.automations.noShowRecovery.language || "es",
        },
      },
    };
  };

  const saveFlow = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!flowDraft.name.trim()) {
      toast.error("Ponle un nombre a la configuración.");
      return;
    }
    const parsedPrice = Number(flowDraft.communicablePrice.trim());
    if (
      flowDraft.hidePriceUntilMeeting
      && (!flowDraft.communicablePrice.trim() || !Number.isFinite(parsedPrice) || parsedPrice <= 0)
    ) {
      toast.error("Para ocultar el precio, registra un precio interno mayor que cero.");
      return;
    }
    if (flowDraft.rejectionAction === "sell_product" && !flowDraft.rejectionFunnelId) {
      toast.error("Selecciona el embudo que recibirá a los prospectos no calificados.");
      return;
    }
    const publicAliases = publicAliasesFromText(flowDraft.publicAliasesText);
    if (publicAliases.length > 20) {
      toast.error("Puedes configurar máximo 20 nombres cortos por flujo.");
      return;
    }
    if (publicAliases.some((alias) => Array.from(alias).length > 80)) {
      toast.error("Cada nombre corto puede tener máximo 80 caracteres.");
      return;
    }
    if (flowDraft.artifactRetentionDays < 1 || flowDraft.artifactRetentionDays > 3650) {
      toast.error("La retención debe estar entre 1 y 3650 días.");
      return;
    }
    if (
      !Number.isInteger(flowDraft.attendanceGraceMinutes)
      || flowDraft.attendanceGraceMinutes < 0
      || flowDraft.attendanceGraceMinutes > 180
    ) {
      toast.error("La espera para marcar una ausencia debe estar entre 0 y 180 minutos.");
      return;
    }
    const enabledAutomations = [
      flowDraft.automations.initialNudge.enabled,
      ...flowDraft.automations.reminders.map((reminder) => reminder.enabled),
      flowDraft.automations.noShowRecovery.enabled,
    ].some(Boolean);
    if (enabledAutomations && approvedTemplatesUnavailable) {
      toast.error("No pudimos comprobar las plantillas aprobadas. Ve a Plantillas, sincronízalas e intenta guardar de nuevo.");
      return;
    }
    if (enabledAutomations && approvedTemplateOptions.length === 0) {
      toast.error("Necesitas al menos una plantilla aprobada. Créala o sincronízala primero en Plantillas.");
      return;
    }
    const templateIsApproved = (name: string, language: string) =>
      approvedTemplateKeys.has(approvedTemplateKey(name, language));
    const initialNudge = flowDraft.automations.initialNudge;
    if (!Number.isInteger(initialNudge.delayMinutes) || initialNudge.delayMinutes < 1 || initialNudge.delayMinutes > 30) {
      toast.error("El primer contacto debe enviarse entre 1 y 30 minutos después del registro.");
      return;
    }
    if (initialNudge.enabled && !templateIsApproved(initialNudge.templateName, initialNudge.language)) {
      toast.error("Selecciona una plantilla aprobada para el primer contacto.");
      return;
    }
    if (flowDraft.automations.reminders.length > 6) {
      toast.error("Puedes configurar máximo 6 recordatorios por cita.");
      return;
    }
    for (const reminder of flowDraft.automations.reminders) {
      if (!Number.isInteger(reminder.minutesBefore) || reminder.minutesBefore < 5 || reminder.minutesBefore > 10080) {
        toast.error("Cada recordatorio debe enviarse entre 5 minutos y 7 días antes de la cita.");
        return;
      }
      if (reminder.enabled && !templateIsApproved(reminder.templateName, reminder.language)) {
        toast.error("Cada recordatorio activo necesita una plantilla aprobada.");
        return;
      }
    }
    const configuredReminderOffsets = flowDraft.automations.reminders
      .filter((reminder) => reminder.enabled || reminder.templateName)
      .map((reminder) => reminder.minutesBefore);
    if (new Set(configuredReminderOffsets).size !== configuredReminderOffsets.length) {
      toast.error("Dos recordatorios configurados no pueden usar el mismo momento de envío.");
      return;
    }
    const noShowRecovery = flowDraft.automations.noShowRecovery;
    if (!Number.isInteger(noShowRecovery.delayMinutes) || noShowRecovery.delayMinutes < 0 || noShowRecovery.delayMinutes > 1440) {
      toast.error("La recuperación de no-show debe enviarse entre 0 y 1440 minutos después del inicio.");
      return;
    }
    if (noShowRecovery.enabled && !templateIsApproved(noShowRecovery.templateName, noShowRecovery.language)) {
      toast.error("Selecciona una plantilla aprobada para recuperar el no-show.");
      return;
    }
    setFlowSaving(true);
    try {
      const saved = flowDraft.id
        ? await appointmentSchedulingService.updateFlow(flowDraft.id, flowPayload())
        : await appointmentSchedulingService.createFlow(flowPayload());
      setFlows((current) => {
        const exists = current.some((flow) => flow.id === saved.id);
        return exists ? current.map((flow) => flow.id === saved.id ? saved : flow) : [...current, saved];
      });
      setFlowDraft(flowToDraft(saved));
      setAvailabilityDraft((current) => ({ ...current, flowId: current.flowId || String(saved.id) }));
      toast.success("Configuración de agendamiento guardada");
    } catch (error) {
      toast.error(apiErrorMessage(error));
    } finally {
      setFlowSaving(false);
    }
  };

  const sellerPayload = (draft: SellerDraft) => ({
    display_name: draft.displayName.trim(),
    email: draft.email.trim(),
    timezone: draft.timezone.trim(),
    status: draft.status,
    meeting_duration_minutes: draft.meetingDurationMinutes,
    buffer_before_minutes: draft.bufferBeforeMinutes,
    buffer_after_minutes: draft.bufferAfterMinutes,
    max_daily_meetings: draft.maxDailyMeetings ? Number(draft.maxDailyMeetings) : null,
    round_robin_weight: draft.roundRobinWeight,
  });

  const createSeller = async (event: React.FormEvent) => {
    event.preventDefault();
    setSellerSavingId("new");
    try {
      const saved = await appointmentSchedulingService.createSeller(sellerPayload(newSeller));
      setSellers((current) => [...current, saved]);
      setSellerDrafts((current) => ({ ...current, [saved.id]: sellerToDraft(saved) }));
      setAvailabilityDraft((current) => ({
        ...current,
        sellerId: current.sellerId || String(saved.id),
      }));
      setCalendarsBySeller((current) => ({ ...current, [saved.id]: [] }));
      setNewSeller((current) => ({ ...current, displayName: "", email: "" }));
      toast.success("Vendedor agregado");
    } catch (error) {
      toast.error(apiErrorMessage(error));
    } finally {
      setSellerSavingId(null);
    }
  };

  const saveSeller = async (sellerId: number) => {
    const draft = sellerDrafts[sellerId];
    if (!draft) return;
    setSellerSavingId(sellerId);
    try {
      const saved = await appointmentSchedulingService.updateSeller(sellerId, sellerPayload(draft));
      setSellers((current) => current.map((seller) => seller.id === sellerId ? saved : seller));
      setSellerDrafts((current) => ({ ...current, [sellerId]: sellerToDraft(saved) }));
      toast.success("Vendedor actualizado");
    } catch (error) {
      toast.error(apiErrorMessage(error));
    } finally {
      setSellerSavingId(null);
    }
  };

  const startSellerGoogle = async (seller: AppointmentSeller) => {
    setGoogleAction(`connect-${seller.id}`);
    try {
      const authorization = await appointmentSchedulingService.authorizeSellerGoogle({
        seller_id: seller.id,
        expected_email: seller.email,
        grant_level: "capture",
      });
      window.location.assign(authorization.authorization_url);
    } catch (error) {
      toast.error(apiErrorMessage(error));
      setGoogleAction(null);
    }
  };

  const probeConnection = async (connection: AppointmentGoogleConnection) => {
    setGoogleAction(`probe-${connection.id}`);
    try {
      const updated = await appointmentSchedulingService.probeGoogleConnection(connection.id);
      setConnections((current) => current.map((item) => item.id === updated.id ? updated : item));
      toast.success(updated.scheduling_ready || updated.admin_reports_scope_ready
        ? "Conexión comprobada"
        : "Google respondió, pero la conexión todavía no está lista");
    } catch (error) {
      toast.error(apiErrorMessage(error));
      const refreshed = await appointmentSchedulingService.getGoogleConnectionReadiness(connection.id).catch(() => null);
      if (refreshed) setConnections((current) => current.map((item) => item.id === refreshed.id ? refreshed : item));
    } finally {
      setGoogleAction(null);
    }
  };

  const reconnectGoogle = async (connection: AppointmentGoogleConnection) => {
    setGoogleAction(`reconnect-${connection.id}`);
    try {
      const authorization = await appointmentSchedulingService.reconnectGoogleConnection(connection.id, {
        grant_level: connection.role === "seller" ? "capture" : undefined,
      });
      window.location.assign(authorization.authorization_url);
    } catch (error) {
      toast.error(apiErrorMessage(error));
      setGoogleAction(null);
    }
  };

  const startCapturePreflight = async (connection: AppointmentGoogleConnection) => {
    setGoogleAction(`capture-${connection.id}`);
    captureReadinessSyncedRef.current.delete(connection.id);
    setCapturePreflightErrors((current) => {
      if (!current[connection.id]) return current;
      const next = { ...current };
      delete next[connection.id];
      return next;
    });
    try {
      const preflight = await appointmentSchedulingService.startGoogleCapturePreflight(connection.id);
      if (!preflight) {
        const message = "La API no devolvió una prueba de captura. El vendedor permanece bloqueado para reparto; actualiza e intenta de nuevo.";
        setCapturePreflightErrors((current) => ({ ...current, [connection.id]: message }));
        toast.error(message);
        return;
      }
      setCapturePreflights((current) => ({ ...current, [connection.id]: preflight }));
      if (preflight.status === "verified") {
        await syncCaptureConnectionReadiness(connection.id);
        toast.success("Grabación y transcripción quedaron verificadas");
      } else {
        toast.success("Verificación iniciada. Fluxi preparará el Meet privado.");
      }
    } catch (error) {
      const message = capturePreflightErrorMessage(error);
      setCapturePreflightErrors((current) => ({ ...current, [connection.id]: message }));
      toast.error(message);
      if (apiErrorStatus(error) === 409) void fetchCapturePreflight(connection.id);
    } finally {
      setGoogleAction(null);
    }
  };

  const bindPrimaryCalendar = async (seller: AppointmentSeller, connection: AppointmentGoogleConnection) => {
    setGoogleAction(`calendar-${seller.id}`);
    try {
      const calendar = await appointmentSchedulingService.createSellerCalendar(seller.id, {
        connection_id: connection.id,
        google_calendar_id: "primary",
        display_name: "Calendario principal",
        timezone: seller.timezone,
        is_write_calendar: true,
        blocks_availability: true,
      });
      setCalendarsBySeller((current) => ({
        ...current,
        [seller.id]: [...(current[seller.id] || []), calendar],
      }));
      toast.success("Calendario principal vinculado");
    } catch (error) {
      toast.error(apiErrorMessage(error));
    } finally {
      setGoogleAction(null);
    }
  };

  const startWorkspaceAdminGoogle = async () => {
    setGoogleAction("workspace-admin");
    try {
      const authorization = await appointmentSchedulingService.authorizeWorkspaceAdminGoogle({
        expected_email: workspaceAdminEmail.trim() || null,
      });
      window.location.assign(authorization.authorization_url);
    } catch (error) {
      toast.error(apiErrorMessage(error));
      setGoogleAction(null);
    }
  };

  const createAvailability = async (event: React.FormEvent) => {
    event.preventDefault();
    const seller = sellers.find((item) => item.id === Number(availabilityDraft.sellerId));
    if (!seller) {
      toast.error("Selecciona un vendedor.");
      return;
    }
    setAvailabilitySaving(true);
    try {
      const saved = await appointmentSchedulingService.createAvailabilityRule({
        seller_id: seller.id,
        flow_id: availabilityDraft.flowId ? Number(availabilityDraft.flowId) : null,
        day_of_week: availabilityDraft.dayOfWeek,
        start_local_time: availabilityDraft.startLocalTime,
        end_local_time: availabilityDraft.endLocalTime,
        spans_midnight: availabilityDraft.startLocalTime > availabilityDraft.endLocalTime,
        timezone: seller.timezone,
        enabled: true,
      });
      setAvailabilityRules((current) => [...current, saved]);
      toast.success("Horario disponible agregado");
    } catch (error) {
      toast.error(apiErrorMessage(error));
    } finally {
      setAvailabilitySaving(false);
    }
  };

  const removeAvailability = async (rule: AppointmentAvailabilityRule) => {
    setAvailabilitySaving(true);
    try {
      await appointmentSchedulingService.deleteAvailabilityRule(rule.id);
      setAvailabilityRules((current) => current.filter((item) => item.id !== rule.id));
      toast.success("Horario eliminado");
    } catch (error) {
      toast.error(apiErrorMessage(error));
    } finally {
      setAvailabilitySaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-64 items-center justify-center text-gray-500">
        <Loader2 className="mr-2 size-5 animate-spin" /> Cargando agendamientos...
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center">
        <AlertTriangle className="mx-auto size-7 text-red-600" />
        <p className="mt-3 text-sm font-semibold text-red-800">No se pudo cargar Agendamientos</p>
        <p className="mt-1 text-sm text-red-700">{loadError}</p>
        <button type="button" onClick={() => void loadAll()} className="mt-4 rounded-full bg-white px-4 py-2 text-sm font-semibold text-red-700 shadow-sm">
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <CalendarClock className="size-5 text-gray-700" />
            <h3 className="text-base font-semibold text-gray-950">Agendamientos</h3>
          </div>
          <p className="mt-1 max-w-3xl text-sm leading-6 text-gray-500">
            Configura cómo el chatbot califica prospectos, confirma reuniones y distribuye espacios entre tus vendedores.
          </p>
        </div>
        <button type="button" onClick={() => void loadAll()} className="inline-flex h-9 items-center gap-2 rounded-full bg-gray-100 px-4 text-sm font-semibold text-gray-700 hover:bg-gray-200">
          <RefreshCw className="size-4" /> Actualizar
        </button>
      </div>
      {flows.some((flow) => flow.status === "active") && readySellerCount === 0 ? (
        <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
          <AlertTriangle className="mt-0.5 size-5 shrink-0" />
          <p>Hay un flujo activo, pero ningún vendedor completó todos los requisitos: Google conectado, un calendario activo de escritura, al menos un calendario activo que bloquee disponibilidad y grabación/transcripción verificadas. El chatbot no podrá ofrecer espacios hasta entonces.</p>
        </div>
      ) : null}

      <Section
        title="Flujo principal de calificación"
        description="El bot conversa para entender si la reunión realmente puede ayudar. Las preguntas son una guía adaptable, no un formulario rígido."
      >
        <div className="mb-5 flex flex-wrap gap-2">
          {flows.map((flow) => (
            <button
              key={flow.id}
              type="button"
              onClick={() => setFlowDraft(flowToDraft(flow))}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                flowDraft.id === flow.id ? "bg-gray-950 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {flow.name}
            </button>
          ))}
          <button type="button" onClick={() => setFlowDraft(emptyFlowDraft())} className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
            <Plus className="size-4" /> Nueva configuración
          </button>
        </div>

        <form onSubmit={saveFlow} className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <label>
              <FieldLabel>Nombre</FieldLabel>
              <input className={textFieldClass} required value={flowDraft.name} onChange={(event) => commitFlowDraft({ name: event.target.value })} />
            </label>
            <label>
              <FieldLabel>Estado</FieldLabel>
              <select className={textFieldClass} value={flowDraft.status} onChange={(event) => commitFlowDraft({ status: event.target.value as FlowDraft["status"] })}>
                <option value="draft">Borrador</option>
                <option value="active">Activo</option>
                <option value="paused">Pausado</option>
                <option value="archived">Archivado</option>
              </select>
            </label>
            <label>
              <FieldLabel>Orden del proceso</FieldLabel>
              <select className={textFieldClass} value={flowDraft.bookingMode} onChange={(event) => commitFlowDraft({ bookingMode: event.target.value as FlowDraft["bookingMode"] })}>
                <option value="qualify_then_book">Calificar y luego agendar</option>
                <option value="prebook_then_qualify">Preagendar y luego confirmar</option>
              </select>
            </label>
            <label>
              <FieldLabel>Zona horaria</FieldLabel>
              <input className={textFieldClass} value={flowDraft.timezone} onChange={(event) => commitFlowDraft({ timezone: event.target.value })} />
            </label>
            <label>
              <FieldLabel>Duración de reunión (minutos)</FieldLabel>
              <input type="number" min={5} max={480} className={textFieldClass} value={flowDraft.meetingDurationMinutes} onChange={(event) => commitFlowDraft({ meetingDurationMinutes: Number(event.target.value) })} />
            </label>
            <label>
              <FieldLabel>Espera para marcar una ausencia (minutos)</FieldLabel>
              <input
                type="number"
                min={0}
                max={180}
                className={textFieldClass}
                value={flowDraft.attendanceGraceMinutes}
                onChange={(event) => commitFlowDraft({ attendanceGraceMinutes: Number(event.target.value) })}
              />
              <span className="mt-1 block text-xs leading-5 text-gray-500">
                Fluxi espera este tiempo después del fin programado antes de permitir “No asistió”. El valor queda congelado en cada cita, aunque luego cambies esta configuración.
              </span>
            </label>
            <label>
              <FieldLabel>Anticipación mínima (minutos)</FieldLabel>
              <input type="number" min={0} className={textFieldClass} value={flowDraft.minimumNoticeMinutes} onChange={(event) => commitFlowDraft({ minimumNoticeMinutes: Number(event.target.value) })} />
            </label>
            <label>
              <FieldLabel>Ventana para agendar (días)</FieldLabel>
              <input type="number" min={1} max={730} className={textFieldClass} value={flowDraft.bookingWindowDays} onChange={(event) => commitFlowDraft({ bookingWindowDays: Number(event.target.value) })} />
            </label>
            <label className="md:col-span-2">
              <FieldLabel>Nombres cortos reconocibles por WhatsApp</FieldLabel>
              <textarea
                className={`${textFieldClass} min-h-20 py-2.5`}
                value={flowDraft.publicAliasesText}
                onChange={(event) => commitFlowDraft({ publicAliasesText: event.target.value })}
                placeholder={"Ej.\nConsultoría\nVSL\nDiagnóstico ecommerce"}
              />
              <span className="mt-1 block text-xs leading-5 text-gray-500">
                Uno por línea, máximo 20 y 80 caracteres por nombre. Las comas se conservan como parte del nombre. Si existen varios flujos, el prospecto puede elegir con el nombre completo o con uno de estos alias; Fluxi nunca le pide IDs internos.
              </span>
            </label>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            {([
              ["financial", "Capacidad financiera", flowDraft.financialCriterionEnabled, flowDraft.financialCriterion, "Ej. Puede invertir entre COP 5 y 10 millones sin depender de financiación."],
              ["awareness", "Conciencia e intención", flowDraft.awarenessCriterionEnabled, flowDraft.awarenessCriterion, "Ej. Reconoce el problema, tiene urgencia y busca resolverlo ahora."],
              ["fit", "Nicho y encaje", flowDraft.fitCriterionEnabled, flowDraft.fitCriterion, "Ej. Tiene un negocio de servicios B2B con oferta validada."],
            ] as const).map(([key, title, enabled, value, placeholder]) => (
              <div key={key} className="rounded-2xl bg-gray-50 p-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-gray-900">{title}</p>
                  <Toggle
                    checked={enabled}
                    label={`Activar ${title}`}
                    onChange={(checked) => commitFlowDraft(
                      key === "financial" ? { financialCriterionEnabled: checked }
                        : key === "awareness" ? { awarenessCriterionEnabled: checked }
                          : { fitCriterionEnabled: checked }
                    )}
                  />
                </div>
                <textarea
                  className={`${textAreaClass} mt-3`}
                  disabled={!enabled}
                  placeholder={placeholder}
                  value={value}
                  onChange={(event) => commitFlowDraft(
                    key === "financial" ? { financialCriterion: event.target.value }
                      : key === "awareness" ? { awarenessCriterion: event.target.value }
                        : { fitCriterion: event.target.value }
                  )}
                />
                {key === "financial" ? (
                  <p className="mt-2 text-xs leading-5 text-gray-500">
                    Esta es una regla interna de calificación. Si contiene una cifra y activas “Ocultar precio”, el chatbot puede usarla para comparar la capacidad declarada, pero no debe revelarla, sugerirla ni repetirla al prospecto.
                  </p>
                ) : null}
              </div>
            ))}
          </div>

          <label className="block">
            <FieldLabel>Cómo debe llevar la conversación</FieldLabel>
            <textarea className={textAreaClass} value={flowDraft.qualificationInstructions} onChange={(event) => commitFlowDraft({ qualificationInstructions: event.target.value })} />
          </label>

          <div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-gray-950">Preguntas guía</p>
                <p className="mt-1 text-xs leading-5 text-gray-500">El bot usa el contexto, pregunta una por vez y evita repetir lo que el prospecto ya explicó. Si dejas la lista vacía, seguirá la ruta adaptativa base definida en el chatbot.</p>
              </div>
              <button type="button" onClick={() => commitFlowDraft({ questions: [...flowDraft.questions, newQuestion()] })} className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200">
                <Plus className="size-4" /> Agregar pregunta
              </button>
            </div>
            <div className="mt-3 space-y-2">
              {flowDraft.questions.map((question, index) => (
                <div key={question.key} className="flex items-center gap-2">
                  <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600">{index + 1}</span>
                  <input
                    className={textFieldClass}
                    value={question.question}
                    placeholder="Escribe una pregunta guía"
                    onChange={(event) => commitFlowDraft({ questions: flowDraft.questions.map((item) => item.key === question.key ? { ...item, question: event.target.value } : item) })}
                  />
                  <button type="button" onClick={() => commitFlowDraft({ questions: flowDraft.questions.filter((item) => item.key !== question.key) })} className="flex size-9 shrink-0 items-center justify-center rounded-full text-gray-400 hover:bg-red-50 hover:text-red-600" title="Eliminar pregunta">
                    <Trash2 className="size-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 p-4">
            <p className="text-sm font-semibold text-gray-950">Precio comunicable</p>
            <div className="mt-3 grid gap-4 sm:grid-cols-[1fr_120px]">
              <label>
                <FieldLabel>{flowDraft.hidePriceUntilMeeting ? "Precio interno (requerido al ocultar)" : "Precio de referencia (opcional)"}</FieldLabel>
                <input type="number" min={0} className={textFieldClass} value={flowDraft.communicablePrice} onChange={(event) => commitFlowDraft({ communicablePrice: event.target.value })} placeholder="0" />
              </label>
              <label>
                <FieldLabel>Moneda</FieldLabel>
                <input maxLength={3} className={textFieldClass} value={flowDraft.communicablePriceCurrency} onChange={(event) => commitFlowDraft({ communicablePriceCurrency: event.target.value.toUpperCase() })} />
              </label>
            </div>
            <div className="mt-4 flex items-start justify-between gap-4 rounded-xl bg-gray-50 p-4">
              <div>
                <p className="text-sm font-semibold text-gray-900">Ocultar precio hasta la reunión</p>
                <p className="mt-1 text-xs leading-5 text-gray-500">
                  {flowDraft.hidePriceUntilMeeting
                    ? "ON: Fluxi retira exactamente esta cifra del catálogo, la base de conocimiento y el historial antes de responder. La regla financiera sigue disponible solo como criterio interno y el chatbot no debe revelarla. No oculta los precios de otros productos."
                    : "OFF: el chatbot puede comunicarlo cuando el prospecto pregunte; no lo fuerza en cada conversación."}
                </p>
              </div>
              <Toggle checked={flowDraft.hidePriceUntilMeeting} onChange={(checked) => commitFlowDraft({ hidePriceUntilMeeting: checked })} label="Ocultar precio hasta la reunión" />
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 p-4">
            <div className="flex gap-3 rounded-xl border border-blue-100 bg-blue-50 p-4 text-blue-950">
              <Video className="mt-0.5 size-5 shrink-0" />
              <div>
                <p className="text-sm font-semibold">Captura nativa de Google, sin bots</p>
                <p className="mt-1 text-xs leading-5 text-blue-800">
                  Grabación y transcripción son requisitos del módulo y no tienen interruptores por flujo. Fluxi solicita su activación cuando Google comprueba que la cuenta del vendedor y esa reunión tienen capacidad real. Las notas inteligentes son opcionales: si Google no las ofrece, no bloquean la cita.
                </p>
              </div>
            </div>

            <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_220px]">
              <label>
                <FieldLabel>Aviso que recibirá el prospecto</FieldLabel>
                <textarea
                  className={textAreaClass}
                  maxLength={5000}
                  value={flowDraft.captureNoticeText}
                  onChange={(event) => commitFlowDraft({ captureNoticeText: event.target.value })}
                  placeholder={DEFAULT_CAPTURE_NOTICE}
                />
                <span className="mt-1 block text-xs leading-5 text-gray-500">
                  Explica con lenguaje claro qué se capturará y para qué. Si queda vacío, Fluxi usa el aviso estándar del ejemplo. Google conserva además sus avisos nativos al entrar a Meet. Revisa este texto con tu asesoría legal según el país y el uso de los datos.
                </span>
              </label>
              <label>
                <FieldLabel>Retención en Fluxi (días)</FieldLabel>
                <input
                  type="number"
                  min={1}
                  max={3650}
                  className={textFieldClass}
                  value={flowDraft.artifactRetentionDays}
                  onChange={(event) => commitFlowDraft({ artifactRetentionDays: Number(event.target.value) })}
                />
                <span className="mt-1 block text-xs leading-5 text-gray-500">
                  Entre 1 y 3650 días. Esta regla controla la evidencia que conserva Fluxi; no cambia la retención propia de Google Workspace.
                </span>
              </label>
            </div>

            <fieldset className="mt-4">
              <legend className="text-xs font-semibold text-gray-600">Cómo comunicar el consentimiento</legend>
              <div className="mt-2 grid gap-3 sm:grid-cols-2">
                <label className={`cursor-pointer rounded-xl border p-4 ${flowDraft.captureConsentMode === "explicit_ack" ? "border-gray-950 bg-gray-50" : "border-gray-200"}`}>
                  <input
                    type="radio"
                    className="sr-only"
                    checked={flowDraft.captureConsentMode === "explicit_ack"}
                    onChange={() => commitFlowDraft({ captureConsentMode: "explicit_ack" })}
                  />
                  <span className="text-sm font-semibold text-gray-900">Pedir aceptación explícita</span>
                  <span className="mt-1 block text-xs leading-5 text-gray-500">El chatbot presenta el aviso y pide una respuesta afirmativa. En cada cita, Fluxi muestra por separado si esa aceptación quedó registrada o sigue pendiente.</span>
                </label>
                <label className={`cursor-pointer rounded-xl border p-4 ${flowDraft.captureConsentMode === "notice" ? "border-gray-950 bg-gray-50" : "border-gray-200"}`}>
                  <input
                    type="radio"
                    className="sr-only"
                    checked={flowDraft.captureConsentMode === "notice"}
                    onChange={() => commitFlowDraft({ captureConsentMode: "notice" })}
                  />
                  <span className="text-sm font-semibold text-gray-900">Informar mediante aviso</span>
                  <span className="mt-1 block text-xs leading-5 text-gray-500">El chatbot comunica el aviso sin solicitar una confirmación adicional. Úsalo solo si tu base legal y política lo permiten.</span>
                </label>
              </div>
            </fieldset>
          </div>

          <div className="rounded-2xl border border-gray-200 p-4">
            <p className="text-sm font-semibold text-gray-950">Si el prospecto no califica</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label className={`cursor-pointer rounded-xl border p-4 ${flowDraft.rejectionAction === "politely_decline" ? "border-gray-950 bg-gray-50" : "border-gray-200"}`}>
                <input type="radio" className="sr-only" checked={flowDraft.rejectionAction === "politely_decline"} onChange={() => commitFlowDraft({ rejectionAction: "politely_decline", rejectionFunnelId: "" })} />
                <span className="text-sm font-semibold text-gray-900">Agradecer y rechazar amablemente</span>
                <span className="mt-1 block text-xs leading-5 text-gray-500">Cierra con respeto, sin hacer sentir juzgada a la persona.</span>
              </label>
              <label className={`cursor-pointer rounded-xl border p-4 ${flowDraft.rejectionAction === "sell_product" ? "border-gray-950 bg-gray-50" : "border-gray-200"}`}>
                <input type="radio" className="sr-only" checked={flowDraft.rejectionAction === "sell_product"} onChange={() => commitFlowDraft({ rejectionAction: "sell_product" })} />
                <span className="text-sm font-semibold text-gray-900">Llevar a un producto</span>
                <span className="mt-1 block text-xs leading-5 text-gray-500">Continúa con un embudo de toma de orden ya configurado.</span>
              </label>
            </div>
            {flowDraft.rejectionAction === "sell_product" ? (
              <label className="mt-4 block">
                <FieldLabel>Embudo de venta</FieldLabel>
                <select className={textFieldClass} value={flowDraft.rejectionFunnelId} onChange={(event) => commitFlowDraft({ rejectionFunnelId: event.target.value })}>
                  <option value="">Seleccionar embudo activo</option>
                  {activeOrderCaptureOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                {commerceFlowsUnavailable ? <p className="mt-2 text-xs text-amber-700">No fue posible consultar los embudos de Ventas. La selección no se puede configurar ahora.</p> : null}
                {!commerceFlowsUnavailable && activeOrderCaptureOptions.length === 0 ? <p className="mt-2 text-xs text-gray-500">Primero crea y activa un flujo en Acciones → Ventas.</p> : null}
              </label>
            ) : null}
          </div>

          <div className="rounded-2xl border border-gray-200 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <Clock3 className="size-4 text-violet-700" />
                  <p className="text-sm font-semibold text-gray-950">Mensajes automáticos de la cita</p>
                </div>
                <p className="mt-1 max-w-3xl text-xs leading-5 text-gray-500">
                  Fluxi programa cada mensaje como un trabajo durable y vuelve a calcularlo cuando la cita cambia. Todos empiezan apagados y solo usan plantillas aprobadas por Meta para esta cuenta.
                </p>
              </div>
              <span className="rounded-full bg-gray-100 px-3 py-1 text-[11px] font-semibold text-gray-600">
                {flowDraft.automations.reminders.length}/6 recordatorios
              </span>
            </div>

            {approvedTemplatesUnavailable ? (
              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs leading-5 text-amber-800">
                No pudimos consultar las plantillas aprobadas. Ve a <strong>Plantillas</strong>, sincronízalas con Meta y vuelve a cargar esta configuración. Fluxi no permitirá guardar una automatización activa sin comprobar su plantilla.
              </div>
            ) : approvedTemplateOptions.length === 0 ? (
              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs leading-5 text-amber-800">
                Esta cuenta todavía no tiene plantillas aprobadas disponibles. Crea o sincroniza una en <strong>Plantillas</strong> antes de activar estos mensajes.
              </div>
            ) : null}

            <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Primer contacto</p>
                  <p className="mt-1 text-xs leading-5 text-gray-500">Escribe por WhatsApp después de que el prospecto deja sus datos y todavía no inicia la conversación.</p>
                </div>
                <Toggle
                  checked={flowDraft.automations.initialNudge.enabled}
                  onChange={(enabled) => commitFlowAutomations({
                    initialNudge: { ...flowDraft.automations.initialNudge, enabled },
                  })}
                  label="Activar primer contacto automático"
                />
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-[180px_1fr]">
                <label>
                  <FieldLabel>Minutos después del registro</FieldLabel>
                  <input
                    type="number"
                    min={1}
                    max={30}
                    className={textFieldClass}
                    value={flowDraft.automations.initialNudge.delayMinutes}
                    onChange={(event) => commitFlowAutomations({
                      initialNudge: {
                        ...flowDraft.automations.initialNudge,
                        delayMinutes: Number(event.target.value),
                      },
                    })}
                  />
                </label>
                <label>
                  <FieldLabel>Plantilla e idioma</FieldLabel>
                  <ApprovedTemplateSelect
                    name={flowDraft.automations.initialNudge.templateName}
                    language={flowDraft.automations.initialNudge.language}
                    options={approvedTemplateOptions}
                    unavailable={approvedTemplatesUnavailable}
                    onChange={(template) => commitFlowAutomations({
                      initialNudge: {
                        ...flowDraft.automations.initialNudge,
                        templateName: template.name,
                        language: template.language,
                      },
                    })}
                  />
                </label>
              </div>
              <p className="mt-2 text-[11px] leading-5 text-gray-500">Rango permitido: 1–30 minutos. Si la persona inicia WhatsApp antes, el trabajo queda obsoleto y no se envía.</p>
            </div>

            <div className="mt-3 rounded-xl border border-gray-200 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Recordatorios previos</p>
                  <p className="mt-1 text-xs leading-5 text-gray-500">Hasta seis momentos distintos entre 5 minutos y 7 días antes de la cita.</p>
                </div>
                <button
                  type="button"
                  disabled={flowDraft.automations.reminders.length >= 6}
                  onClick={() => commitFlowAutomations({
                    reminders: [...flowDraft.automations.reminders, newReminder()],
                  })}
                  className="inline-flex h-9 items-center gap-2 rounded-full border border-gray-200 px-3 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Plus className="size-3.5" /> Agregar recordatorio
                </button>
              </div>
              {!flowDraft.automations.reminders.length ? (
                <p className="mt-3 rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-4 text-center text-xs text-gray-500">No hay recordatorios configurados.</p>
              ) : null}
              <div className="mt-3 space-y-3">
                {flowDraft.automations.reminders.map((reminder, index) => (
                  <div key={reminder.key} className="rounded-xl border border-gray-100 bg-gray-50 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <Toggle
                          checked={reminder.enabled}
                          onChange={(enabled) => updateFlowReminder(reminder.key, { enabled })}
                          label={`Activar recordatorio ${index + 1}`}
                        />
                        <span className="text-xs font-semibold text-gray-700">Recordatorio {index + 1}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => commitFlowAutomations({
                          reminders: flowDraft.automations.reminders.filter((item) => item.key !== reminder.key),
                        })}
                        className="flex size-8 items-center justify-center rounded-full text-gray-400 hover:bg-red-50 hover:text-red-600"
                        title="Eliminar recordatorio"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </div>
                    <div className="mt-3 grid gap-3 md:grid-cols-[180px_1fr]">
                      <label>
                        <FieldLabel>Minutos antes</FieldLabel>
                        <input
                          type="number"
                          min={5}
                          max={10080}
                          className={textFieldClass}
                          value={reminder.minutesBefore}
                          onChange={(event) => updateFlowReminder(reminder.key, {
                            minutesBefore: Number(event.target.value),
                          })}
                        />
                      </label>
                      <label>
                        <FieldLabel>Plantilla e idioma</FieldLabel>
                        <ApprovedTemplateSelect
                          name={reminder.templateName}
                          language={reminder.language}
                          options={approvedTemplateOptions}
                          unavailable={approvedTemplatesUnavailable}
                          onChange={(template) => updateFlowReminder(reminder.key, {
                            templateName: template.name,
                            language: template.language,
                          })}
                        />
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Recuperación de no-show</p>
                  <p className="mt-1 text-xs leading-5 text-gray-500">Retoma la conversación solo después de que una fuente explícita o verificada confirma que el invitado no asistió.</p>
                </div>
                <Toggle
                  checked={flowDraft.automations.noShowRecovery.enabled}
                  onChange={(enabled) => commitFlowAutomations({
                    noShowRecovery: { ...flowDraft.automations.noShowRecovery, enabled },
                  })}
                  label="Activar recuperación de no-show"
                />
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-[180px_1fr]">
                <label>
                  <FieldLabel>Minutos desde el inicio</FieldLabel>
                  <input
                    type="number"
                    min={0}
                    max={1440}
                    className={textFieldClass}
                    value={flowDraft.automations.noShowRecovery.delayMinutes}
                    onChange={(event) => commitFlowAutomations({
                      noShowRecovery: {
                        ...flowDraft.automations.noShowRecovery,
                        delayMinutes: Number(event.target.value),
                      },
                    })}
                  />
                </label>
                <label>
                  <FieldLabel>Plantilla e idioma</FieldLabel>
                  <ApprovedTemplateSelect
                    name={flowDraft.automations.noShowRecovery.templateName}
                    language={flowDraft.automations.noShowRecovery.language}
                    options={approvedTemplateOptions}
                    unavailable={approvedTemplatesUnavailable}
                    onChange={(template) => commitFlowAutomations({
                      noShowRecovery: {
                        ...flowDraft.automations.noShowRecovery,
                        templateName: template.name,
                        language: template.language,
                      },
                    })}
                  />
                </label>
              </div>
              <p className="mt-2 text-[11px] leading-5 text-gray-500">El mensaje nunca se programa antes del fin de la cita más la espera de asistencia, aunque este retraso desde el inicio sea menor. El reloj no convierte por sí solo una cita en no-show.</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="max-w-2xl text-xs leading-5 text-gray-500">El briefing privado se construye con objetivo, dolor, obstáculo, capacidad/autoridad y resistencias. Los mensajes automáticos se cancelan o recalculan cuando el estado o la hora de la cita dejan obsoleto el envío.</p>
            <SaveButton loading={flowSaving} label={flowDraft.id ? "Guardar cambios" : "Crear configuración"} />
          </div>
        </form>
      </Section>

      <Section
        title="Vendedores y sus cuentas Google"
        description="Cada vendedor conecta su propio Google: esa cuenta organiza la reunión y mantiene su disponibilidad real. No se usa una cuenta central para crear todas las citas."
      >
        <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm leading-6 text-blue-900">
          <div className="flex gap-3">
            <Video className="mt-0.5 size-5 shrink-0" />
            <div>
              <p className="font-semibold">Una cuenta Google por vendedor; captura nativa, sin bots</p>
              <p className="mt-1 text-blue-800">
                Cada vendedor autoriza su propia cuenta. Esa cuenta crea la invitación de Calendar, organiza el Meet y conserva su disponibilidad real; Fluxi no entra a la llamada como participante ni usa una cuenta central para grabar. Tener permisos concedidos no demuestra que la licencia permita grabar o transcribir: esas capacidades se muestran como verificadas solo después de una comprobación real de Meet.
              </p>
            </div>
          </div>
        </div>
        {googleLoadError ? (
          <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            La configuración del flujo sigue disponible, pero Google no pudo cargarse: {googleLoadError}
          </div>
        ) : null}

        <form onSubmit={createSeller} className="mt-5 grid gap-3 rounded-2xl bg-gray-50 p-4 md:grid-cols-[1fr_1fr_180px_auto] md:items-end">
          <label>
            <FieldLabel>Nombre del vendedor</FieldLabel>
            <input required className={textFieldClass} value={newSeller.displayName} onChange={(event) => setNewSeller((current) => ({ ...current, displayName: event.target.value }))} />
          </label>
          <label>
            <FieldLabel>Correo de su Google</FieldLabel>
            <input required type="email" className={textFieldClass} value={newSeller.email} onChange={(event) => setNewSeller((current) => ({ ...current, email: event.target.value }))} />
          </label>
          <label>
            <FieldLabel>Zona horaria</FieldLabel>
            <input className={textFieldClass} value={newSeller.timezone} onChange={(event) => setNewSeller((current) => ({ ...current, timezone: event.target.value }))} />
          </label>
          <SaveButton loading={sellerSavingId === "new"} label="Agregar" />
        </form>

        <div className="mt-5 space-y-4">
          {sellers.length === 0 ? <p className="rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500">Agrega al menos un vendedor para habilitar disponibilidad.</p> : null}
          {sellers.map((seller) => {
            const draft = sellerDrafts[seller.id];
            if (!draft) return null;
            const connection = connections.find((item) => item.role === "seller" && item.seller_id === seller.id && item.status !== "revoked");
            const calendars = calendarsBySeller[seller.id] || [];
            const writeCalendar = calendars.find((calendar) => calendar.is_write_calendar && calendar.status === "active");
            const blockingCalendar = calendars.find((calendar) => calendar.blocks_availability && calendar.status === "active");
            const readiness = connection ? readinessCopy(connection) : null;
            const capturePreflight = connection ? capturePreflights[connection.id] : null;
            const capturePreflightError = connection ? capturePreflightErrors[connection.id] : undefined;
            return (
              <article key={seller.id} className="rounded-2xl border border-gray-200 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="flex size-10 items-center justify-center rounded-full bg-gray-100"><UserRound className="size-5 text-gray-600" /></span>
                    <div>
                      <p className="text-sm font-semibold text-gray-950">{seller.display_name}</p>
                      <p className="text-xs text-gray-500">{seller.email}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {readiness ? <span className={`rounded-full px-3 py-1 text-xs font-semibold ${readiness.tone}`}>{readiness.label}</span> : <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600">Google sin conectar</span>}
                    {writeCalendar ? <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">Calendario de escritura vinculado</span> : null}
                    {blockingCalendar ? <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">Bloqueo de disponibilidad activo</span> : null}
                    {writeCalendar && !blockingCalendar ? <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">Falta calendario bloqueante</span> : null}
                  </div>
                </div>

                <div className="mt-4 grid gap-3 md:grid-cols-4">
                  <label><FieldLabel>Nombre</FieldLabel><input className={textFieldClass} value={draft.displayName} onChange={(event) => setSellerDrafts((current) => ({ ...current, [seller.id]: { ...draft, displayName: event.target.value } }))} /></label>
                  <label><FieldLabel>Correo Google esperado</FieldLabel><input type="email" className={textFieldClass} value={draft.email} onChange={(event) => setSellerDrafts((current) => ({ ...current, [seller.id]: { ...draft, email: event.target.value } }))} /></label>
                  <label><FieldLabel>Duración (min)</FieldLabel><input type="number" min={5} className={textFieldClass} value={draft.meetingDurationMinutes} onChange={(event) => setSellerDrafts((current) => ({ ...current, [seller.id]: { ...draft, meetingDurationMinutes: Number(event.target.value) } }))} /></label>
                  <label><FieldLabel>Máximo diario</FieldLabel><input type="number" min={1} className={textFieldClass} value={draft.maxDailyMeetings} placeholder="Sin límite" onChange={(event) => setSellerDrafts((current) => ({ ...current, [seller.id]: { ...draft, maxDailyMeetings: event.target.value } }))} /></label>
                  <label><FieldLabel>Zona horaria</FieldLabel><input className={textFieldClass} value={draft.timezone} onChange={(event) => setSellerDrafts((current) => ({ ...current, [seller.id]: { ...draft, timezone: event.target.value } }))} /></label>
                  <label><FieldLabel>Estado</FieldLabel><select className={textFieldClass} value={draft.status} onChange={(event) => setSellerDrafts((current) => ({ ...current, [seller.id]: { ...draft, status: event.target.value as SellerDraft["status"] } }))}><option value="active">Activo</option><option value="inactive">Inactivo</option><option value="disconnected">Desconectado</option></select></label>
                  <label><FieldLabel>Buffer antes (min)</FieldLabel><input type="number" min={0} className={textFieldClass} value={draft.bufferBeforeMinutes} onChange={(event) => setSellerDrafts((current) => ({ ...current, [seller.id]: { ...draft, bufferBeforeMinutes: Number(event.target.value) } }))} /></label>
                  <label><FieldLabel>Buffer después (min)</FieldLabel><input type="number" min={0} className={textFieldClass} value={draft.bufferAfterMinutes} onChange={(event) => setSellerDrafts((current) => ({ ...current, [seller.id]: { ...draft, bufferAfterMinutes: Number(event.target.value) } }))} /></label>
                  <label><FieldLabel>Peso de reparto</FieldLabel><input type="number" min={1} className={textFieldClass} value={draft.roundRobinWeight} onChange={(event) => setSellerDrafts((current) => ({ ...current, [seller.id]: { ...draft, roundRobinWeight: Number(event.target.value) } }))} /></label>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <button type="button" disabled={sellerSavingId === seller.id} onClick={() => void saveSeller(seller.id)} className="inline-flex h-9 items-center gap-2 rounded-full bg-gray-100 px-4 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-60">
                    {sellerSavingId === seller.id ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />} Guardar vendedor
                  </button>
                  {!connection ? (
                    <button type="button" disabled={googleAction === `connect-${seller.id}`} onClick={() => void startSellerGoogle(seller)} className="inline-flex h-9 items-center gap-2 rounded-full bg-gray-950 px-4 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-60">
                      {googleAction === `connect-${seller.id}` ? <Loader2 className="size-4 animate-spin" /> : <Link2 className="size-4" />} Conectar Calendar, Meet y captura
                    </button>
                  ) : (
                    <>
                      <button type="button" disabled={googleAction === `probe-${connection.id}`} onClick={() => void probeConnection(connection)} className="inline-flex h-9 items-center gap-2 rounded-full border border-gray-200 px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60">
                        {googleAction === `probe-${connection.id}` ? <Loader2 className="size-4 animate-spin" /> : <CheckCircle2 className="size-4" />} Comprobar conexión
                      </button>
                      {connection.status !== "connected" || connection.meet_space_create_scope_granted !== true ? (
                        <button
                          type="button"
                          disabled={googleAction === `reconnect-${connection.id}`}
                          onClick={() => void reconnectGoogle(connection)}
                          className="inline-flex h-9 items-center gap-2 rounded-full border border-gray-200 px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                        >
                          {googleAction === `reconnect-${connection.id}` ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
                          {connection.status === "connected" ? "Autorizar captura" : "Reconectar"}
                        </button>
                      ) : null}
                      {connection.scheduling_ready && !writeCalendar ? (
                        <button type="button" disabled={googleAction === `calendar-${seller.id}`} onClick={() => void bindPrimaryCalendar(seller, connection)} className="inline-flex h-9 items-center gap-2 rounded-full bg-gray-950 px-4 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-60">
                          {googleAction === `calendar-${seller.id}` ? <Loader2 className="size-4 animate-spin" /> : <CalendarClock className="size-4" />} Usar calendario principal
                        </button>
                      ) : null}
                    </>
                  )}
                </div>
                {connection ? (
                  <div className="mt-4 grid gap-3 lg:grid-cols-2">
                    <div className="rounded-xl bg-gray-50 p-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Permisos y agenda</p>
                      <dl className="mt-2 space-y-2 text-xs text-gray-700">
                        <div className="flex justify-between gap-3"><dt>Cuenta conectada</dt><dd className="break-all text-right font-semibold">{connection.google_email}</dd></div>
                        <div className="flex justify-between gap-3"><dt>Calendar operativo</dt><dd className="font-semibold">{connection.scheduling_ready ? "Sí" : "No"}</dd></div>
                        <div className="flex justify-between gap-3"><dt>Crear Meet privado</dt><dd className="font-semibold">{connection.meet_space_create_scope_granted ? "Permiso concedido" : "Sin permiso"}</dd></div>
                        <div className="flex justify-between gap-3"><dt>Configurar Meet</dt><dd className="font-semibold">{connection.meet_settings_scope_granted ? "Permiso concedido" : "Sin permiso"}</dd></div>
                        <div className="flex justify-between gap-3"><dt>Leer artefactos</dt><dd className="font-semibold">{connection.meet_artifacts_read_scope_granted ? "Permiso concedido" : "Sin permiso"}</dd></div>
                        <div className="flex justify-between gap-3"><dt>Scope restringido de Drive</dt><dd className="font-semibold">No requerido en V1</dd></div>
                      </dl>
                      <p className="mt-3 text-[11px] leading-5 text-gray-500">Los permisos autorizan a Fluxi a consultar Google, pero por sí solos no prueban que la licencia de esta cuenta pueda generar cada artefacto.</p>
                    </div>
                    <div className="rounded-xl bg-gray-50 p-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Capacidad observada en Meet</p>
                      <div className="mt-2 space-y-2">
                        <CapabilityState label="Identidad del vendedor" status={connection.meet_identity_ready ? "verified" : "unknown"} />
                        <CapabilityState label="Configuración automática" status={connection.meet_settings_status} />
                        <CapabilityState label="Grabación" status={connection.recording_readiness_status} />
                        <CapabilityState label="Transcripción" status={connection.transcription_readiness_status} />
                        <CapabilityState label="Notas inteligentes (opcional)" status={connection.smart_notes_readiness_status} />
                        <CapabilityState label="Almacenamiento" status={connection.storage_readiness_status} />
                      </div>
                      <p className="mt-3 text-[11px] leading-5 text-gray-500">
                        {connection.capabilities_verified_at
                          ? `Última evidencia: ${formatDateTime(connection.capabilities_verified_at)}`
                          : "Aún no existe una comprobación de capacidad ligada a una reunión real."}
                      </p>
                      {connection.capability_error ? (
                        <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-[11px] leading-5 text-red-700">
                          Google reportó: {connection.capability_error.replace(/_/g, " ")}
                        </p>
                      ) : null}
                    </div>
                    <CapturePreflightPanel
                      connection={connection}
                      preflight={capturePreflight}
                      error={capturePreflightError}
                      busy={googleAction === `capture-${connection.id}` || Boolean(captureReadinessLoading[connection.id])}
                      checkingReadiness={Boolean(captureReadinessLoading[connection.id])}
                      onStart={() => void startCapturePreflight(connection)}
                    />
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      </Section>

      <Section
        title="Disponibilidad"
        description="Estos horarios son la base local; Google Calendar bloquea automáticamente los espacios que ya estén ocupados."
      >
        <form onSubmit={createAvailability} className="grid gap-3 md:grid-cols-[1fr_1fr_140px_120px_120px_auto] md:items-end">
          <label>
            <FieldLabel>Vendedor</FieldLabel>
            <select required className={textFieldClass} value={availabilityDraft.sellerId} onChange={(event) => setAvailabilityDraft((current) => ({ ...current, sellerId: event.target.value }))}>
              <option value="">Seleccionar</option>
              {sellers.filter((seller) => seller.status === "active").map((seller) => <option key={seller.id} value={seller.id}>{seller.display_name}</option>)}
            </select>
          </label>
          <label>
            <FieldLabel>Aplicar al flujo</FieldLabel>
            <select className={textFieldClass} value={availabilityDraft.flowId} onChange={(event) => setAvailabilityDraft((current) => ({ ...current, flowId: event.target.value }))}>
              <option value="">Todos los flujos</option>
              {flows.filter((flow) => flow.status !== "archived").map((flow) => <option key={flow.id} value={flow.id}>{flow.name}</option>)}
            </select>
          </label>
          <label>
            <FieldLabel>Día</FieldLabel>
            <select className={textFieldClass} value={availabilityDraft.dayOfWeek} onChange={(event) => setAvailabilityDraft((current) => ({ ...current, dayOfWeek: Number(event.target.value) }))}>
              {dayNames.map((day, index) => <option key={day} value={index}>{day}</option>)}
            </select>
          </label>
          <label><FieldLabel>Desde</FieldLabel><input type="time" className={textFieldClass} value={availabilityDraft.startLocalTime} onChange={(event) => setAvailabilityDraft((current) => ({ ...current, startLocalTime: event.target.value }))} /></label>
          <label><FieldLabel>Hasta</FieldLabel><input type="time" className={textFieldClass} value={availabilityDraft.endLocalTime} onChange={(event) => setAvailabilityDraft((current) => ({ ...current, endLocalTime: event.target.value }))} /></label>
          <button type="submit" disabled={availabilitySaving} className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-gray-950 px-4 text-sm font-semibold text-white disabled:opacity-60">
            {availabilitySaving ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />} Agregar
          </button>
        </form>
        <div className="mt-4 space-y-2">
          {availabilityRules.length === 0 ? <p className="rounded-xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500">Todavía no hay horarios configurados.</p> : null}
          {availabilityRules.map((rule) => {
            const seller = sellers.find((item) => item.id === rule.seller_id);
            const flow = flows.find((item) => item.id === rule.flow_id);
            return (
              <div key={rule.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-gray-50 px-4 py-3">
                <div className="flex items-center gap-3">
                  <Clock3 className="size-4 text-gray-500" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{dayNames[rule.day_of_week]} · {rule.start_local_time.slice(0, 5)}–{rule.end_local_time.slice(0, 5)}</p>
                    <p className="text-xs text-gray-500">{seller?.display_name || `Vendedor ${rule.seller_id}`} · {flow?.name || "Todos los flujos"} · {rule.timezone}</p>
                  </div>
                </div>
                <button type="button" disabled={availabilitySaving} onClick={() => void removeAvailability(rule)} className="flex size-8 items-center justify-center rounded-full text-gray-400 hover:bg-red-50 hover:text-red-600" title="Eliminar horario"><Trash2 className="size-4" /></button>
              </div>
            );
          })}
        </div>
      </Section>

      <Section
        title="Google Workspace Admin (opcional)"
        description="Esta cuenta no organiza ni graba reuniones. Solo consulta la auditoría del dominio para comprobar si el correo externo esperado aparece en la reunión exacta; no identifica tarjetas, speakers ni duraciones. Cada vendedor sigue conectando su propia cuenta."
      >
        {workspaceAdminConnection ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-gray-50 p-4">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold text-gray-900">{workspaceAdminConnection.google_email}</p>
                {workspaceAdminReadiness ? (
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${workspaceAdminReadiness.tone}`}>
                    {workspaceAdminReadiness.label}
                  </span>
                ) : null}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {workspaceAdminConnection.verified_attendance_ready
                  ? "Esta conexión ya produjo evidencia exacta de un correo externo en una reunión vinculada. No atribuye sesiones ni duración a ese correo."
                  : workspaceAdminConnection.admin_reports_scope_ready
                    ? "El permiso de Reports está concedido, pero todavía no existe una comprobación completa de asistencia."
                    : "Pendiente de comprobar permisos de Google Workspace Reports."}
              </p>
            </div>
            <button type="button" disabled={googleAction === `probe-${workspaceAdminConnection.id}`} onClick={() => void probeConnection(workspaceAdminConnection)} className="inline-flex h-9 items-center gap-2 rounded-full border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60">
              {googleAction === `probe-${workspaceAdminConnection.id}` ? <Loader2 className="size-4 animate-spin" /> : <CheckCircle2 className="size-4" />} Comprobar permisos
            </button>
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-end">
            <label>
              <FieldLabel>Correo del administrador del Workspace</FieldLabel>
              <input type="email" className={textFieldClass} value={workspaceAdminEmail} onChange={(event) => setWorkspaceAdminEmail(event.target.value)} placeholder="admin@tuempresa.com" />
            </label>
            <button type="button" disabled={googleAction === "workspace-admin"} onClick={() => void startWorkspaceAdminGoogle()} className="inline-flex h-10 items-center justify-center gap-2 rounded-full border border-gray-200 px-5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60">
              {googleAction === "workspace-admin" ? <Loader2 className="size-4 animate-spin" /> : <Link2 className="size-4" />} Conectar admin
            </button>
          </div>
        )}
        <p className="mt-3 text-xs leading-5 text-gray-500">El permiso de Reports y una coincidencia exacta del correo son estados distintos. Fluxi nunca interpreta una invitación aceptada o la ausencia de auditoría como prueba de asistencia o no-show.</p>
      </Section>
    </div>
  );
};

export default AppointmentSchedulingSettings;
